import { useCallback, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { enqueueSnackbar } from 'notistack';
import { Box, Grid2, Typography } from '@mui/material';

import AppConfig from '@/src/config/AppConfig';
import AttributesConfig, { IAttributeDef } from '@/src/config/AttributesConfig';
import DynamicBreadCrumbs from '@/src/components/common/DynamicBreadCrumbs';
import DynamicInput from '@/src/components/common/DynamicInput';
import SaveAndCancel from '@/src/components/common/SaveAndCancel';
import NoAuthorization from '@/src/components/common/NoAuthorization';
import { RootState } from '@/src/datastore/store';
import UseNavigation from '@/src/hooks/useNavigation';
import AllPolicyService from '@/src/services/allPolicy/allPolicyService';
import SchemaValidations from '@/src/utils/SchemaValidations';
import {
  INPUT_FIELD_TYPE_MAP,
  UI_TYPE_INPUT_TYPE_MAP,
  INPUT_TYPES_ENUM,
  ILooseObject
} from '@/src/types/common.interface';
import lightTheme from '@/src/styles/themes/lightTheme';
import AppMessages from '@/src/config/message_config/AppMessages';
import MvelCodeEditorComponent from '@/src/components/mvel-editor/MvelCodeEditorComponent';
import MVELTestCases from '@/src/components/mvel-editor/MVELTestCases';
import { mvelEditorConfiguration } from '@/src/config/mvel/MvelEditorSchemas';

import { TestCaseFormValues } from '@/src/components/mvel-editor/TestCaseAccordion';

const AddPolicy = () => {
  const navigation = UseNavigation();
  const [inputOptions, setInputOptions] = useState<ILooseObject>({});
  const [loadingTestCases, setLoadingTestCases] = useState<Record<string, boolean>>({});
  const [testCaseResults, setTestCaseResults] = useState<Record<string, any>>({});

  const inputSchema = mvelEditorConfiguration.MVEL_INPUT_SCHEMA;

  interface IRichEditorConfigProps {
    toolbarOptions?: Array<Array<ILooseObject | string>>;
    formats?: string[];
  }

  const richEditorConfig: IRichEditorConfigProps = {
    toolbarOptions: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }]
    ]
  };

  const availableApplications = useSelector(
    (state: RootState) => state?.userDetails?.OptionsAndPermissions?.applicationsOptions
  );

  // Add permission selector
  const userAppPermissions: ILooseObject = useSelector(
    (state: RootState) => state?.userDetails?.OptionsAndPermissions?.appPermissions
  );

  // Add permission check function
  const getAppPermissions = () => {
    return Object.entries(userAppPermissions as Record<string, string[]>)?.reduce(
      (acc: string[], [key, permissions]: [string, string[]]) => {
        acc = Array.from(new Set([...acc, ...permissions]));
        return acc;
      },
      []
    );
  };

  const policySchema = SchemaValidations.AddPolicyFieldsSchema(AttributesConfig.ADD_POLICY_INPUT);
  const testCasesSchema = SchemaValidations.AddTestCasesSchema();

  const {
    control: addPolicyControl,
    handleSubmit: handleAddPolicySubmit,
    formState: {
      errors: addPolicyErrors,
      isSubmitting: isAddPolicySubmitting,
      isDirty: isAddPolicyDirty
    },
    clearErrors: clearAddPolicyErrors,
    reset: resetAddPolicy,
    watch: watchAddPolicyForm
  } = useForm<ILooseObject>({
    defaultValues: {
      ...AttributesConfig.ADD_POLICY_INPUT?.reduce(
        (acc: ILooseObject, attribute: IAttributeDef) => {
          acc[attribute.name] = attribute.default_value ?? '';
          return acc;
        },
        {}
      ),
      policy_value: mvelEditorConfiguration.MVEL_EDITOR_DEFAULT_SCRIPT
    },
    resolver: yupResolver(policySchema, { abortEarly: false }) as any,
    mode: 'onBlur'
  });

  const {
    control: testCasesControl,
    formState: { errors: testCasesErrors, isDirty: isTestCasesDirty },
    clearErrors: clearTestCasesErrors,
    reset: resetTestCases,
    watch: watchTestCasesForm,
    trigger: triggerTestCases
  } = useForm<TestCaseFormValues>({
    defaultValues: {
      testCases: []
    },
    resolver: yupResolver(testCasesSchema, { abortEarly: false }) as any,
    mode: 'onBlur'
  });

  const createNewPolicy = async (data: ILooseObject) => {
    const testCasesData = watchTestCasesForm().testCases;
    const selectedApplicationKey = data.application;
    const updatedPayload = Object.entries(data).reduce((acc: ILooseObject, [key, value]) => {
      if (value || typeof value === 'number') {
        if (key === 'policy_value') {
          acc.policy_code_encoded = btoa(value);
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});
    delete updatedPayload.application;
    (updatedPayload as any).testCases = testCasesData;

    try {
      const results = await AllPolicyService.createNewPolicy({
        applicationKey: selectedApplicationKey ?? '',
        payload: updatedPayload
      });

      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
      } else if (!results.error.code) {
        enqueueSnackbar(AppMessages.Success.policyAdded, {
          variant: 'success',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.success
        });
        navigation.navigateTo('/policy');
      }
    } catch (error) {
      enqueueSnackbar(JSON.stringify(error), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    }
  };

  const handleRunTestCase = useCallback(
    async (index: number) => {
      const policyValues = watchAddPolicyForm();
      const testCasesValues = watchTestCasesForm();
      const testCase = testCasesValues.testCases[index];
      const mvelCode = policyValues.policy_value || '';
      const policyCodeEncoded = btoa(mvelCode);

      let apiConfig: any = {};
      try {
        apiConfig = testCase.api_config ? JSON.parse(testCase.api_config) : {};
      } catch (error) {
        enqueueSnackbar('Invalid API Config JSON', {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
        return;
      }

      const headers: Record<string, string> = {};
      testCase.headers.forEach((h: any) => {
        if (h.key) headers[h.key.toLowerCase()] = h.value;
      });

      const params: Record<string, string> = {};
      testCase.params.forEach((p: any) => {
        if (p.key) params[p.key] = p.value;
      });

      const userAttributes: Record<string, any> = {};
      testCase.user_attributes.forEach((attr: any) => {
        if (attr.key) userAttributes[attr.key] = attr.value;
      });

      const authHeader = {
        bearerToken: headers['authorization'] || null,
        apiKey: null,
        isAuthHeaderPresent: !!headers['authorization']
      };

      let body = {};
      if (testCase.body) {
        try {
          body = JSON.parse(testCase.body);
        } catch (error) {
          enqueueSnackbar('Invalid JSON in Request Body. Please provide a valid JSON object.', {
            variant: 'error',
            autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
          });
          return;
        }
      }

      const payload = {
        policyCodeEncoded,
        testData: {
          webRequest: {
            method: apiConfig?.method?.toUpperCase() || 'GET',
            body,
            headers,
            parameters: params,
            authHeader,
            uri: apiConfig?.uri || ''
          },
          data: {
            metadata: {
              attributes: userAttributes
            }
          },
          appKey: policyValues.application || '',
          orgKeyList: testCase.organization ? [testCase.organization] : [],
          apiConfig: {
            ...apiConfig,
            permissions: apiConfig?.permissions || [],
            policy: apiConfig?.policy || null
          },
          policyData: null
        }
      };

      setLoadingTestCases((prev) => ({ ...prev, [testCase.id]: true }));
      setTestCaseResults((prev) => ({ ...prev, [testCase.id]: null }));
      try {
        const result = await AllPolicyService.executePolicy({
          body: payload
        });
        if (result.error.message) {
          enqueueSnackbar(result.error.message, { variant: 'error' });
          setTestCaseResults((prev) => ({
            ...prev,
            [testCase.id]: { error: result.error.message }
          }));
        } else {
          enqueueSnackbar('Test executed successfully', { variant: 'success' });
          setTestCaseResults((prev) => ({ ...prev, [testCase.id]: { success: result.data } }));
        }
      } catch (error) {
        enqueueSnackbar(`Failed to execute test: ${error}`, { variant: 'error' });
        setTestCaseResults((prev) => ({
          ...prev,
          [testCase.id]: { error: 'Failed to execute test' }
        }));
      } finally {
        setLoadingTestCases((prev) => ({ ...prev, [testCase.id]: false }));
      }
    },
    [watchAddPolicyForm, watchTestCasesForm]
  );

  useEffect(() => {
    if (availableApplications) {
      setInputOptions((prev) => ({
        ...prev,
        application: Object.values(availableApplications)
      }));
    }
  }, [availableApplications]);

  if (!getAppPermissions()?.includes('policy_create')) {
    return <NoAuthorization />;
  }

  return (
    <Box>
      <DynamicBreadCrumbs
        breadCrumbsDetails={[
          { key: 1, name: 'All Policies', redirectPath: '/policy' },
          { key: 2, name: 'Add Policy' }
        ]}
      />
      <Typography variant="h5" sx={{ my: 2 }} color="primary.dark">
        Add Policy
      </Typography>
      <Box
        border={1}
        borderColor={lightTheme.palette.grey[300]}
        borderRadius={3}
        sx={{ p: 2, display: 'flex', flexDirection: 'column', rowGap: 1 }}
      >
        <form onSubmit={handleAddPolicySubmit(createNewPolicy)}>
          <Grid2 container columnSpacing={10} spacing={2}>
            {AttributesConfig.ADD_POLICY_INPUT.filter((attribute) => attribute.is_visible).map(
              (attribute) => {
                if (attribute.name === 'policy_value') {
                  return null;
                }
                const inputType = UI_TYPE_INPUT_TYPE_MAP[attribute.ui_type.toUpperCase()];
                const options = attribute.options ?? inputOptions?.[attribute.name];
                const inputFieldType: INPUT_TYPES_ENUM =
                  INPUT_FIELD_TYPE_MAP[attribute?.value_type?.toUpperCase()];
                const isVisible: boolean = !!attribute?.is_visible;
                if (!isVisible) {
                  return null;
                }
                const isRequired = attribute?.is_mandatory;
                const isPolicyValueModified =
                  watchAddPolicyForm('policy_value') !==
                  mvelEditorConfiguration.MVEL_EDITOR_DEFAULT_SCRIPT;

                return (
                  <Grid2
                    container
                    size={{ xs: 6 }}
                    key={attribute?.attribute_id}
                    alignItems="center"
                  >
                    {/* UI label */}
                    <Grid2 size={{ xs: 4 }} display="flex" alignItems="center">
                      <Box sx={{ textAlign: 'left', pb: 4 }}>
                        <Typography>{`${attribute?.ui_label} ${isRequired ? '*' : ''}`}</Typography>
                      </Box>
                    </Grid2>
                    {/* DynamicInput and RichEditor */}
                    <Grid2 size={{ xs: 8 }}>
                      <Controller
                        name={attribute.name as string}
                        control={addPolicyControl}
                        render={({ field: { onChange, onBlur, value, name } }) => (
                          <DynamicInput
                            type={inputType}
                            id={attribute.attribute_id}
                            label={attribute.ui_label}
                            value={value}
                            hasError={!!(addPolicyErrors?.[name]?.message as string)}
                            helperText={(addPolicyErrors?.[name]?.message as string) ?? ' '}
                            options={options}
                            loading={false}
                            maxLength={attribute.max_length}
                            placeholder={attribute.placeholder}
                            disableClearable={attribute.disable_clearable}
                            disabled={attribute.name === 'application' && isPolicyValueModified}
                            onChange={(e: React.FormEvent<HTMLInputElement>) => {
                              onChange(e);
                              clearAddPolicyErrors(name);
                            }}
                            onBlur={onBlur}
                            inputType={inputFieldType}
                            richEditorProps={richEditorConfig}
                            minWidth={100}
                          />
                        )}
                      />
                    </Grid2>
                  </Grid2>
                );
              }
            )}
            <Grid2 size={12}>
              <Controller
                name="policy_value"
                control={addPolicyControl}
                render={({ field: { onChange, value } }) => (
                  <>
                    <MvelCodeEditorComponent
                      value={value}
                      inputSchema={inputSchema}
                      onValidation={() => {}}
                      onChange={onChange}
                    />
                    {addPolicyErrors.policy_value && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {addPolicyErrors.policy_value.message as string}
                      </Typography>
                    )}
                  </>
                )}
              />
            </Grid2>
          </Grid2>
          {getAppPermissions()?.includes('policy_create') && (
            <SaveAndCancel
              handleCancel={() => {
                clearAddPolicyErrors();
                resetAddPolicy();
                clearTestCasesErrors();
                resetTestCases();
                setTestCaseResults({});
              }}
              isLoading={isAddPolicySubmitting}
              disableSave={
                isAddPolicySubmitting ||
                Object.keys(addPolicyErrors).length > 0 ||
                (!isAddPolicyDirty &&
                  !isTestCasesDirty &&
                  watchAddPolicyForm('policy_value') ===
                    mvelEditorConfiguration.MVEL_EDITOR_DEFAULT_SCRIPT)
              }
              disableReset={
                !isAddPolicyDirty &&
                !isTestCasesDirty &&
                watchAddPolicyForm('policy_value') ===
                  mvelEditorConfiguration.MVEL_EDITOR_DEFAULT_SCRIPT &&
                Object.keys(addPolicyErrors).length === 0
              }
            />
          )}
          <MVELTestCases
            control={testCasesControl}
            errors={testCasesErrors}
            watch={watchTestCasesForm}
            trigger={triggerTestCases}
            handleRunTestCase={handleRunTestCase}
            clearErrors={clearTestCasesErrors}
            applicationKey={watchAddPolicyForm('application')}
            loadingTestCases={loadingTestCases}
            testCaseResults={testCaseResults}
          />
        </form>
      </Box>
    </Box>
  );
};

export default AddPolicy;
